/**
 * MaterialManager — Runtime material swap + animation system.
 *
 * Tracks named materials on the scene graph, swaps them to high-quality
 * presets on init, and animates transitions between colors/finishes.
 *
 * Design decisions:
 * - Each managed material keeps a reference to its original (for reset).
 * - The material.name is preserved after swap so all scene-graph lookups
 *   by name continue to work.
 * - Lerp runs via tick() which is called from SceneManager's useFrame —
 *   no separate RAF loop, stays in sync with the Three.js render cycle.
 * - FlakesTexture stays active in all finish types — only normalScale
 *   changes — eliminates texture-swap artifacts during type transitions.
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

import * as THREE from 'three';
import { materialLibrary } from '../materials/MaterialLibrary';
import type { MaterialApplyOptions, TextureVariantOptions } from '../api/types/material';
import { devWarn, devInfo } from '../utils/devLog';
import type { ManagedSlot, ManagedAtlasSlot, LerpTask, AssetLoader } from './materialManagerTypes';
import { lerp, easeInOutCubic } from './materialManagerTypes';
import { applyDownFacingAO } from '../materials/DownFacingAO';
import { applyMinRoughnessClamp } from '../materials/MinRoughnessClamp';

// ---------------------------------------------------------------------------
// MaterialManager
// ---------------------------------------------------------------------------

export class MaterialManager {
  /** Preset Track — map of material name → slot. */
  private slots = new Map<string, ManagedSlot>();
  /** Active lerp tasks (updated each tick). */
  private lerpTasks: LerpTask[] = [];

  // Atlas Track — texture-based variants (e.g. interior atlas swaps)
  private atlasSlots = new Map<string, ManagedAtlasSlot>();
  private _assetLoader: AssetLoader | null = null;

  /**
   * Called when a visual state change needs at least one render frame.
   * The R3F ticker component wires this to `invalidate()` so that
   * `frameloop="demand"` kicks the first frame after an API call.
   */
  onNeedsRender?: () => void;

  // -------------------------------------------------------------------------
  // init
  // -------------------------------------------------------------------------

  /**
   * Traverse the scene, find all meshes whose material.name is in `targets`,
   * store originals, and immediately swap to the default CarPaint preset.
   *
   * @param scene   - The root Three.js object (typically the loaded GLTF scene)
   * @param targets - List of material names to manage (e.g. ['M_CarPaint_Primary'])
   * @param initialPreset - Preset to swap to on init. Default: 'car-paint'
   * @param initialType   - Finish type for initial swap. Default: 'metallic'
   */
  init(
    scene: THREE.Object3D,
    targets: string[],
    initialPreset = 'car-paint',
    initialType: string = 'metallic',
  ): void {
    const targetSet = new Set(targets);
    const meshMap = new Map<string, { meshes: THREE.Mesh[]; original: THREE.Material }>();

    // Single traversal: collect target meshes AND diagnostic material names
    const allMatNames = new Set<string>();
    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mat = obj.material as THREE.Material;
      if (Array.isArray(mat)) return;

      if (mat.name) allMatNames.add(mat.name);

      if (targetSet.has(mat.name)) {
        const entry = meshMap.get(mat.name) ?? { meshes: [], original: mat };
        entry.meshes.push(obj);
        meshMap.set(mat.name, entry);
      }
    });

    // Diagnostics (log + warn) — runs AFTER the single traversal
    const sorted = [...allMatNames].sort();
    devInfo('materialManager.init()', 'Scene traversal complete.', {
      'Materials found in GLB': sorted,
      'Requested targets': [...targetSet],
    });
    const missing = [...targetSet].filter((t) => !allMatNames.has(t));
    if (missing.length > 0) {
      devWarn(
        'materialManager.init()',
        'One or more targets were not found in the scene — they will be silently ignored.',
        {
          'Not found': missing,
          'Available materials': sorted,
          Hint: 'Material names are case-sensitive. Verify the name in your 3D tool (e.g. Blender, Maya) or check the "Materials found in GLB" list above.',
        },
      );
    }

    for (const [name, { meshes, original }] of meshMap) {
      // Read color from original if it has one
      const srcColor =
        original instanceof THREE.MeshStandardMaterial
          ? '#' + original.color.getHexString()
          : '#888888';

      // For glass preset: carry over original opacity so Case 3
      // ("just upgrade to high-quality glass") preserves the original look.
      const extraOpts: Record<string, unknown> = { type: initialType };
      if (initialPreset === 'glass' && original instanceof THREE.MeshStandardMaterial) {
        const phys = original as THREE.MeshPhysicalMaterial;
        // Convert from transmission-based originals or keep raw opacity.
        extraOpts.originalOpacity =
          phys.transmission != null && phys.transmission > 0
            ? 1 - phys.transmission
            : original.opacity;
      }

      // Create the preset material, preserve the original name
      const swapped = materialLibrary.create(
        initialPreset,
        srcColor,
        extraOpts,
      ) as THREE.MeshPhysicalMaterial;
      swapped.name = name;

      // Shader effect: down-facing AO on freshly created preset materials
      // Skip AO for glass — AO darkens gl_FragColor which destroys transparency.
      if (initialPreset !== 'glass') {
        applyDownFacingAO(swapped, { strength: 0.8, color: [0, 0, 0] });
        applyMinRoughnessClamp(swapped);
      }

      // Assign to all meshes
      for (const mesh of meshes) {
        mesh.material = swapped;
      }

      this.slots.set(name, {
        meshes,
        current: swapped,
        original,
        isSwapped: true,
        activePreset: initialPreset,
        activeType: initialType,
      });
    }
  }

  // -------------------------------------------------------------------------
  // apply (decision matrix)
  // -------------------------------------------------------------------------

  /**
   * Apply material changes to a named target.
   *
   * Decision matrix:
   * - `preset` provided + not yet swapped → swap first, then set values
   * - `preset` provided + already swapped → skip re-swap, lerp values
   * - only `colorCode` → lerp color only
   * - only `type` → lerp PBR + normalScale to preset values
   * - `colorCode` + `type` → lerp both simultaneously
   *
   * @param target - Exact material name (e.g. 'M_CarPaint_Primary')
   * @param opts   - Options (see MaterialApplyOptions)
   */
  apply(target: string, opts: MaterialApplyOptions): void {
    const slot = this.slots.get(target);
    if (!slot) {
      devWarn('engine.setMaterial()', `target "${target}" is not managed.`, {
        'Received target': target,
        'Managed targets': [...this.slots.keys()],
        Hint: 'The target must match a material name registered during init(). Check the "Materials found in GLB" log or ensure init() was called after sceneLoaded.',
      });
      return;
    }

    const {
      preset,
      colorCode,
      type,
      transparency,
      doubleSide,
      animated = true,
      duration = 900,
    } = opts;

    // --- Swap decision ---
    // Only trigger a full material swap when:
    //   - First-time swap (not yet swapped)
    //   - Preset has changed (e.g. car-paint → carbon)
    //   - Type changed AND the library can't lerp that type change
    //     (getTypeValues returns undefined → carbon glossy↔matte needs a swap)
    // Car-paint type changes (metallic↔uni↔matte) return real PBR values, so
    // they go through the lerp path below instead of triggering a swap.
    const typeRequiresSwap =
      preset !== undefined &&
      type !== undefined &&
      slot.activeType !== type &&
      slot.activePreset === preset && // same preset, different type
      materialLibrary.getTypeValues(preset, type) === undefined;

    const needsSwap =
      preset && (!slot.isSwapped || slot.activePreset !== preset || typeRequiresSwap);
    if (needsSwap) {
      const srcColor =
        slot.original instanceof THREE.MeshStandardMaterial
          ? '#' + slot.original.color.getHexString()
          : (colorCode ?? '#888888');

      this.cancelLerp(slot.current);

      // Build factory opts. For glass: carry over original opacity (Case 3)
      const factoryOpts: Record<string, unknown> = { type };
      if (transparency != null) {
        factoryOpts.transparency = transparency;
      }
      if (doubleSide != null) {
        factoryOpts.doubleSide = doubleSide;
      }
      if (preset === 'glass' && transparency == null) {
        const orig = slot.original;
        if (orig instanceof THREE.MeshStandardMaterial) {
          const phys = orig as THREE.MeshPhysicalMaterial;
          factoryOpts.originalOpacity =
            phys.transmission != null && phys.transmission > 0
              ? 1 - phys.transmission
              : orig.opacity;
        }
      }

      const swapped = materialLibrary.create(
        preset,
        srcColor,
        factoryOpts,
      ) as THREE.MeshPhysicalMaterial;
      swapped.name = target;

      // Shader effect: down-facing AO — skip for glass (AO destroys transparency)
      if (preset !== 'glass') {
        applyDownFacingAO(swapped, { strength: 0.8, color: [0, 0, 0] });
        applyMinRoughnessClamp(swapped);
      }

      // Dispose the previous swapped preset material to free GPU resources.
      // Only dispose if we own it (isSwapped=true) — originals belong to the GLB cache.
      if (slot.isSwapped) {
        slot.current.dispose();
      }

      for (const mesh of slot.meshes) {
        mesh.material = swapped;
        // Ensure glass panes always render after opaque geometry and write depth.
        // ModelComponent only patches the original GLB material; we must also
        // enforce this on the swapped preset so sorting is correct post-swap.
        if (preset === 'glass') {
          mesh.renderOrder = 10;
        }
      }

      slot.current = swapped;
      slot.isSwapped = true;
      slot.activePreset = preset;
      slot.activeType = type ?? null;

      // Cross-track sync: keep atlas slot's current in step with the new
      // preset material so applyAtlasVariant() patches the right object.
      const atlasSlot = this.atlasSlots.get(target);
      if (atlasSlot) {
        atlasSlot.current = swapped as THREE.MeshStandardMaterial;
      }

      // Color override: apply immediately (no lerp on a fresh swap)
      if (colorCode) slot.current.color.set(colorCode);
      this.onNeedsRender?.();
      return;
    }

    // --- Build lerp task ---
    if (!colorCode && !type && doubleSide == null) return; // Nothing to do

    // Immediate side toggle — no lerp needed, just flip the flag.
    if (doubleSide != null) {
      slot.current.side = doubleSide ? THREE.DoubleSide : THREE.FrontSide;
      slot.current.needsUpdate = true;
      this.onNeedsRender?.();
    }

    if (!colorCode && !type) return; // Only side was toggled, done.

    // Cancel any existing lerp on this material
    this.cancelLerp(slot.current);

    const task: LerpTask = {
      material: slot.current,
      startTime: performance.now(),
      duration: animated ? duration : 0,
    };

    if (colorCode) {
      task.fromColor = slot.current.color.clone();
      task.toColor = new THREE.Color(colorCode);
    }

    // Track what changed so onComplete can update slot state
    let pendingType: string | null = slot.activeType;
    let pendingPreset: string | null = slot.activePreset;

    if (type) {
      // Resolve lerp-target values via the library — preset-agnostic.
      // Returns undefined for presets that don't support type-only lerp
      // (e.g. carbon, where type changes are handled as full swaps above).
      const activePreset = preset ?? slot.activePreset ?? '';
      const values = materialLibrary.getTypeValues(activePreset, type);
      if (values) {
        task.fromProps = {
          metalness: slot.current.metalness,
          roughness: slot.current.roughness,
          clearcoat: slot.current.clearcoat,
          clearcoatRoughness: slot.current.clearcoatRoughness,
        };
        task.toProps = {
          metalness: values.metalness,
          roughness: values.roughness,
          clearcoat: values.clearcoat,
          clearcoatRoughness: values.clearcoatRoughness,
        };
        if (values.normalScale !== undefined) {
          task.fromNormalScale = slot.current.normalScale.x;
          task.toNormalScale = values.normalScale;
        }
        pendingType = type;
      }
    }
    if (preset) pendingPreset = preset;

    // onComplete: update slot state so subsequent calls see the correct
    // activeType/activePreset and don't misfire a swap.
    task.onComplete = () => {
      slot.activeType = pendingType;
      slot.activePreset = pendingPreset;
    };

    if (!animated || duration <= 0) {
      // Apply immediately and flush slot state right away
      this.applyLerpTask(task, 1);
      task.onComplete();
    } else {
      this.lerpTasks.push(task);
    }
    // Kick the first render frame so the ticker's useFrame loop starts
    this.onNeedsRender?.();
  }

  // -------------------------------------------------------------------------
  // reset
  // -------------------------------------------------------------------------

  /**
   * Restore the original material (from GLB) for a given target.
   * Cancels any active lerp for that material.
   */
  reset(target: string): void {
    const slot = this.slots.get(target);
    if (!slot) {
      devWarn('engine.resetMaterial()', `target "${target}" is not managed.`, {
        'Received target': target,
        'Managed targets': [...this.slots.keys()],
        Hint: 'Only materials registered at init() can be reset. Check the "Materials found in GLB" log.',
      });
      return;
    }

    this.cancelLerp(slot.current);

    // Dispose the swapped preset material to free GPU resources.
    if (slot.isSwapped) {
      slot.current.dispose();
    }

    for (const mesh of slot.meshes) {
      mesh.material = slot.original;
    }

    slot.current = slot.original as THREE.MeshPhysicalMaterial;
    slot.isSwapped = false;
    slot.activePreset = null;
    slot.activeType = null;
    this.onNeedsRender?.();
  }

  // -------------------------------------------------------------------------
  // tick (called from SceneManager useFrame)
  // -------------------------------------------------------------------------

  /**
   * Advance all active lerp animations by one frame.
   * Must be called each frame from the R3F render loop.
   */
  tick(): void {
    if (this.lerpTasks.length === 0) return;

    const now = performance.now();
    const remaining: LerpTask[] = [];

    for (const task of this.lerpTasks) {
      const elapsed = now - task.startTime;
      const t = Math.min(elapsed / task.duration, 1);
      const eased = easeInOutCubic(t);

      this.applyLerpTask(task, eased);
      // NOTE: needsUpdate removed — Three.js tracks uniform changes\n      // (color, metalness, roughness, etc.) without version bumping.
      // Setting needsUpdate per-frame caused ~720 unnecessary shader-recompile
      // checks during a typical color animation.

      if (t < 1) {
        remaining.push(task);
      } else {
        task.onComplete?.();
      }
    }

    this.lerpTasks = remaining;
  }

  // -------------------------------------------------------------------------
  // dispose
  // -------------------------------------------------------------------------

  /** Release all references. Call when engine is destroyed. */
  dispose(): void {
    this.lerpTasks = [];

    // Dispose swapped preset materials (we created them, we clean them up).
    // The original GLB materials are NOT disposed — they belong to the GLTF asset.
    for (const slot of this.slots.values()) {
      if (slot.isSwapped) {
        slot.current.dispose();
      }
    }
    this.slots.clear();

    // Dispose atlas working clones (we cloned them, we clean them up).
    for (const slot of this.atlasSlots.values()) {
      slot.current.dispose();
    }
    this.atlasSlots.clear();
    this._assetLoader = null;
  }

  // -------------------------------------------------------------------------
  // Introspection (for tests / store)
  // -------------------------------------------------------------------------

  /** Returns true if the target is currently managed. */
  has(target: string): boolean {
    return this.slots.has(target);
  }

  /** Returns true if the target has been swapped to a preset material. */
  isSwapped(target: string): boolean {
    return this.slots.get(target)?.isSwapped ?? false;
  }

  /** Number of currently active lerp tasks. */
  get activeLerpCount(): number {
    return this.lerpTasks.length;
  }

  // -------------------------------------------------------------------------
  // Atlas Track — texture-based material variants
  // -------------------------------------------------------------------------

  /**
   * Inject the AssetLoader so applyAtlasVariant() can load textures.
   * Called from createEngine() after the loader is ready.
   */
  setAssetLoader(loader: AssetLoader): void {
    this._assetLoader = loader;
  }

  /**
   * Register materials for texture-based Atlas swaps WITHOUT applying a preset.
   * The original GLB material is cloned once so texture assignments never
   * mutate the shared GLTF asset cache.
   *
   * Only MeshStandardMaterial (or subclass) originals are accepted —
   * non-standard materials are skipped with a warning.
   *
   * @param scene   - Root Three.js object (typically the loaded GLTF scene)
   * @param targets - Material names to manage (e.g. ['M_Interior_Seat'])
   */
  initAtlas(scene: THREE.Object3D, targets: string[]): void {
    if (targets.length === 0) return;
    const targetSet = new Set(targets);

    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mat = obj.material as THREE.Material;
      if (Array.isArray(mat) || !targetSet.has(mat.name)) return;

      if (!(mat instanceof THREE.MeshStandardMaterial)) {
        devWarn(
          'materialManager.initAtlas()',
          `"${mat.name}" is not a MeshStandardMaterial — skipping.`,
          {
            'Material type': mat.type,
            Hint: 'Atlas track requires MeshStandardMaterial or MeshPhysicalMaterial.',
          },
        );
        return;
      }

      const existing = this.atlasSlots.get(mat.name);
      if (existing) {
        existing.meshes.push(obj);
        // All meshes with the same material name must share the same working
        // clone — otherwise only the first mesh gets atlas/lerp updates.
        obj.material = existing.current;
        return;
      }

      const working = mat.clone() as THREE.MeshStandardMaterial;
      working.name = mat.name;
      obj.material = working;

      // Cross-track sync: if the preset track already manages this material,
      // point its `current` to our new clone so color lerp / apply() continue
      // to operate on the material that is actually visible on the mesh.
      const presetSlot = this.slots.get(mat.name);
      if (presetSlot) {
        presetSlot.current = working as THREE.MeshPhysicalMaterial;
      }

      this.atlasSlots.set(mat.name, {
        meshes: [obj],
        original: mat,
        current: working,
        activeDiffuseUrl: null,
        activeNormalUrl: null,
        activeMetallicRoughnessUrl: null,
      });
    });
  }

  /**
   * Apply a texture-based atlas variant to a tracked material.
   *
   * Textures are loaded via AssetLoader (cache-first, deduped by URL).
   * They come from the frontend/CDN at runtime — not built into the engine.
   *
   * @param target - Exact material name registered via initAtlas()
   * @param opts   - Textures to apply + optional colour tint
   */
  async applyAtlasVariant(target: string, opts: TextureVariantOptions): Promise<void> {
    const slot = this.atlasSlots.get(target);
    if (!slot) {
      devWarn('materialManager.applyAtlasVariant()', `target "${target}" is not an atlas slot.`, {
        'Received target': target,
        'Atlas slots': [...this.atlasSlots.keys()],
        Hint: 'Call initAtlas() with this target name after sceneLoaded.',
      });
      return;
    }

    if (!this._assetLoader) {
      devWarn(
        'materialManager.applyAtlasVariant()',
        'No AssetLoader set — call setAssetLoader() in createEngine().',
      );
      return;
    }

    const { diffuseUrl, normalUrl, metallicRoughnessUrl, colorCode } = opts;
    if (!diffuseUrl && !normalUrl && !metallicRoughnessUrl && !colorCode) return;

    const loader = this._assetLoader;
    const [diffuseTex, normalTex, mrTex] = await Promise.all([
      diffuseUrl
        ? (loader.loadTexture(diffuseUrl) as Promise<THREE.Texture>)
        : Promise.resolve(null),
      normalUrl ? (loader.loadTexture(normalUrl) as Promise<THREE.Texture>) : Promise.resolve(null),
      metallicRoughnessUrl
        ? (loader.loadTexture(metallicRoughnessUrl) as Promise<THREE.Texture>)
        : Promise.resolve(null),
    ]);

    if (diffuseTex) {
      // glTF meshes are authored with flipY=false (V origin = top-left, glTF spec).
      // THREE.TextureLoader defaults to flipY=true (WebGL bottom-left convention).
      // Mismatched flipY causes the V-axis to be inverted → wrong atlas region sampled.
      diffuseTex.flipY = false;
      diffuseTex.colorSpace = THREE.SRGBColorSpace;
      diffuseTex.needsUpdate = true;
      slot.current.map = diffuseTex;
      slot.activeDiffuseUrl = diffuseUrl ?? null;
    }

    if (normalTex) {
      normalTex.flipY = false;
      normalTex.colorSpace = THREE.LinearSRGBColorSpace;
      normalTex.needsUpdate = true;
      slot.current.normalMap = normalTex;
      slot.activeNormalUrl = normalUrl ?? null;
    }

    if (mrTex) {
      mrTex.flipY = false;
      mrTex.colorSpace = THREE.LinearSRGBColorSpace;
      mrTex.needsUpdate = true;
      // glTF convention: single texture for both metalness and roughness.
      // Three.js reads metalness from the B channel and roughness from the G channel.
      slot.current.metalnessMap = mrTex;
      slot.current.roughnessMap = mrTex;
      // Set metalness/roughness to 1 so the map values are used directly.
      slot.current.metalness = 1;
      slot.current.roughness = 1;
      slot.activeMetallicRoughnessUrl = metallicRoughnessUrl ?? null;
    }

    if (colorCode) {
      slot.current.color.set(colorCode);
    }

    slot.current.needsUpdate = true;
    this.onNeedsRender?.();
  }

  /**
   * Restore the original (GLB) material for an atlas-tracked target.
   * A fresh clone is installed so the slot remains usable after reset.
   */
  resetAtlas(target: string): void {
    const slot = this.atlasSlots.get(target);
    if (!slot) return;

    // Dispose the current working clone before replacing it
    slot.current.dispose();

    const fresh = slot.original.clone() as THREE.MeshStandardMaterial;
    fresh.name = target;

    for (const mesh of slot.meshes) {
      mesh.material = fresh;
    }

    slot.current = fresh;
    slot.activeDiffuseUrl = null;
    slot.activeNormalUrl = null;
    slot.activeMetallicRoughnessUrl = null;
    this.onNeedsRender?.();
  }

  /** Returns true if the target is managed as an atlas slot. */
  hasAtlas(target: string): boolean {
    return this.atlasSlots.has(target);
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private applyLerpTask(task: LerpTask, t: number): void {
    const mat = task.material;

    if (task.fromColor && task.toColor) {
      mat.color.lerpColors(task.fromColor, task.toColor, t);
    }

    if (task.fromProps && task.toProps) {
      mat.metalness = lerp(task.fromProps.metalness, task.toProps.metalness, t);
      mat.roughness = lerp(task.fromProps.roughness, task.toProps.roughness, t);
      mat.clearcoat = lerp(task.fromProps.clearcoat, task.toProps.clearcoat, t);
      mat.clearcoatRoughness = lerp(
        task.fromProps.clearcoatRoughness,
        task.toProps.clearcoatRoughness,
        t,
      );
    }

    if (task.fromNormalScale !== undefined && task.toNormalScale !== undefined) {
      const scale = lerp(task.fromNormalScale, task.toNormalScale, t);
      mat.normalScale.set(scale, scale);
    }
  }

  private cancelLerp(material: THREE.MeshPhysicalMaterial): void {
    this.lerpTasks = this.lerpTasks.filter((t) => t.material !== material);
  }
}
